/**
 * sl-8n5m.9.2: HeroBanner ViewComponent
 *
 * Title + optional Subtitle + optional Image + optional CTA. Image binding
 * accepts a resolved URL string; materialization resolves BlobReferences to
 * signed imgproxy URLs before handing data to components.
 */
import { createElement } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewRuntimeContext,
} from '../../../types/view-component.types';
import { PlaceholderWarning } from '../../custom-app/placeholder-warning';
import {
  resolveResponsiveImage,
  ResponsiveImage,
} from '../../responsive-image/responsive-image';
import * as styles from './hero-banner.css';

const configSchema = z.object({
  eyebrow: z.string().default(''),
  aiHint: z.string().default(''),
  suggestedSections: z.array(z.string()).default([]),
  titleField: z.string().default('title'),
  subtitleField: z.string().default('subtitle'),
  imageField: z.string().default('image'),
  altField: z.string().default('alt'),
  ctaLabelField: z.string().default('ctaLabel'),
  ctaUrlField: z.string().default('ctaUrl'),
});

export const HeroBannerComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.HERO_BANNER,
  type: 'content',
  name: 'Hero Banner',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '🏞'
  ),
  capabilities: [
    {
      role: 'hero',
      description: 'Hero banner with title, subtitle, image, and CTA',
      required: true,
      requirements: [
        { field: 'title', acceptedTypes: ['text'], required: true },
        { field: 'subtitle', acceptedTypes: ['text'], required: false },
        {
          field: 'image',
          acceptedTypes: ['attachment', 'url'],
          required: false,
        },
        { field: 'alt', acceptedTypes: ['text'], required: false },
        { field: 'ctaLabel', acceptedTypes: ['text'], required: false },
        { field: 'ctaUrl', acceptedTypes: ['url', 'text'], required: false },
      ],
    },
  ],
  configSchema,
  defaultConfig: {
    titleField: 'title',
    subtitleField: 'description',
    imageField: 'image',
    altField: 'alt',
    ctaLabelField: 'ctaLabel',
    ctaUrlField: 'ctaUrl',
  },
  render({ config, data, mode, runtimeContext }) {
    const cfg = config as z.infer<typeof configSchema>;
    const row = data[0];
    const workspaceId = readWorkspaceId(runtimeContext);

    const title =
      row && typeof row[cfg.titleField] === 'string'
        ? (row[cfg.titleField] as string).trim()
        : '';
    if (!title) {
      return createElement(PlaceholderWarning, {
        reasonKey: 'titleMissing',
        role: 'hero',
      });
    }

    const subtitle =
      row && typeof row[cfg.subtitleField] === 'string'
        ? (row[cfg.subtitleField] as string)
        : '';
    const imageCell = row ? row[cfg.imageField] : null;
    const image = resolveResponsiveImage(imageCell, {
      workspaceId,
      allowWorkspaceBlobFallback: Boolean(workspaceId),
    });
    const altField = cfg.altField ?? 'alt';
    const alt =
      row && typeof row[altField] === 'string'
        ? (row[altField] as string).trim()
        : '';
    const ctaLabel =
      row && typeof row[cfg.ctaLabelField] === 'string'
        ? (row[cfg.ctaLabelField] as string).trim()
        : '';
    const ctaUrl =
      row && typeof row[cfg.ctaUrlField] === 'string'
        ? (row[cfg.ctaUrlField] as string).trim()
        : '';
    const hasCta = ctaLabel !== '' && ctaUrl !== '';

    return createElement(
      'section',
      {
        className: styles.hero,
        ...(image ? { 'data-sl-has-image': '' } : {}),
      },
      image &&
        createElement(ResponsiveImage, {
          image: imageCell,
          role: 'hero',
          alt,
          priority: true,
          workspaceId,
          allowWorkspaceBlobFallback: Boolean(workspaceId),
          className: styles.heroImage,
        }),
      image && createElement('div', { className: styles.heroOverlay }),
      createElement(
        'div',
        { className: styles.heroBody },
        cfg.eyebrow
          ? createElement(
              'p',
              { className: styles.heroEyebrow, 'data-sl-role': 'eyebrow' },
              cfg.eyebrow
            )
          : null,
        createElement(
          'h1',
          {
            className: styles.heroTitle,
            'data-sl-field': 'title',
          },
          title
        ),
        subtitle &&
          createElement(
            'p',
            {
              className: styles.heroSubtitle,
              'data-sl-field': 'subtitle',
            },
            subtitle
          ),
        hasCta &&
          createElement(
            'a',
            {
              className: styles.heroCta,
              href: ctaUrl,
              'data-sl-field': 'cta',
            },
            ctaLabel
          ),
        mode !== 'public' && cfg.aiHint
          ? createElement(
              'aside',
              {
                className: styles.heroAiHint,
                'data-sl-role': 'ai-design-hint',
              },
              createElement('span', { 'aria-hidden': 'true' }, '✦'),
              createElement(
                'div',
                null,
                createElement('p', null, cfg.aiHint),
                cfg.suggestedSections.length > 0
                  ? createElement(
                      'div',
                      { className: styles.heroSuggestions },
                      ...cfg.suggestedSections.map(section =>
                        createElement('span', { key: section }, section)
                      )
                    )
                  : null
              )
            )
          : null
      )
    );
  },
  palettePreview() {
    return createElement(
      'div',
      {
        style: {
          padding: 'var(--sl-custom-app-padding-sm)',
          fontSize: 'var(--sl-custom-app-font-size-xs)',
          color: 'var(--streamlain-text-secondary-color)',
        },
      },
      '🏞 Hero'
    );
  },
};

function readWorkspaceId(
  runtimeContext?: ViewRuntimeContext
): string | undefined {
  const value = runtimeContext?.customApp?.['workspaceId'];
  return typeof value === 'string' && value.trim() !== ''
    ? value.trim()
    : undefined;
}
